import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Search, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { useMeetingStore } from "@/lib/meeting-store";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { useOwnerStore } from "@/lib/owner-store";

interface CompletedMeeting {
  id: string;
  team_member: {
    id: string;
    name: string;
  };
  team_member_id?: string;
  meeting_date: string;
  agenda_items: {
    id: string;
    type: string[];
    discussion_points: string;
    next_steps: string;
    blockers: string;
    notes: string;
    created_at: string;
    updated_at: string;
    meeting_id?: string;
  }[];
}

interface Owner {
  id: string;
  name: string;
}

export default function CompletedMeetings() {
  const [meetings, setMeetings] = useState<CompletedMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMeeting, setEditingMeeting] = useState<CompletedMeeting | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { updateMeeting, deleteMeeting } = useMeetingStore();
  const { owners, fetchOwners } = useOwnerStore();

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch meetings with team member details
      const { data: meetingsData, error: meetingsError } = await supabase
        .from('one_on_one_meetings')
        .select(`
          id,
          meeting_date,
          team_member_id,
          team_member:team_member_id(id, name)
        `)
        .eq('status', 'completed')
        .order('meeting_date', { ascending: false });

      if (meetingsError) {
        console.error('Error fetching meetings:', meetingsError);
        throw meetingsError;
      }

      // Fetch agenda items for each meeting
      const meetingsWithDetails = await Promise.all(
        (meetingsData || []).map(async (meeting) => {
          const { data: agendaItems, error: agendaError } = await supabase
            .from('meeting_agenda_items')
            .select(`
              id,
              meeting_id,
              type,
              discussion_points,
              next_steps,
              blockers,
              notes,
              created_at,
              updated_at
            `)
            .eq('meeting_id', meeting.id);

          if (agendaError) {
            console.error('Error fetching agenda items:', agendaError);
            throw agendaError;
          }

          return {
            ...meeting,
            meeting_date: new Date(meeting.meeting_date).toISOString().split('T')[0],
            agenda_items: agendaItems || []
          };
        })
      );

      setMeetings(meetingsWithDetails);
    } catch (err) {
      console.error('Error in fetchMeetings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch meetings');
      toast.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleDelete = async (meeting: CompletedMeeting) => {
    try {
      setIsDeleting(true);
      await deleteMeeting(meeting.id);
      setMeetings(meetings.filter(m => m.id !== meeting.id));
      setShowDeleteDialog(false);
      toast.success('Meeting deleted successfully');
    } catch (err) {
      console.error('Error deleting meeting:', err);
      toast.error('Failed to delete meeting');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async (meeting: CompletedMeeting) => {
    try {
      setIsUpdating(true);
      const formattedDate = new Date(meeting.meeting_date).toISOString().split('T')[0];
      
      // Update the main meeting record
      const { error: meetingError } = await supabase
        .from('one_on_one_meetings')
        .update({
          meeting_date: formattedDate,
          team_member_id: meeting.team_member.id
        })
        .eq('id', meeting.id);

      if (meetingError) {
        console.error('Error updating meeting:', meetingError);
        throw meetingError;
      }

      // Update each agenda item
      for (const item of meeting.agenda_items) {
        const { error: agendaError } = await supabase
          .from('meeting_agenda_items')
          .update({
            discussion_points: item.discussion_points,
            next_steps: item.next_steps,
            blockers: item.blockers,
            notes: item.notes
          })
          .eq('id', item.id)
          .eq('meeting_id', meeting.id);

        if (agendaError) {
          console.error('Error updating agenda item:', agendaError);
          throw agendaError;
        }
      }
      
      await fetchMeetings();
      setShowEditDialog(false);
      setEditingMeeting(null);
      toast.success('Meeting updated successfully');
    } catch (error) {
      console.error('Error updating meeting:', error);
      toast.error('Failed to update meeting');
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredMeetings = useMemo(() => 
    meetings.filter(meeting => {
      const searchLower = searchTerm.toLowerCase();
      return (
        meeting.agenda_items?.some(item => 
          item.discussion_points?.toLowerCase().includes(searchLower) ||
          item.next_steps?.toLowerCase().includes(searchLower) ||
          item.blockers?.toLowerCase().includes(searchLower) ||
          item.notes?.toLowerCase().includes(searchLower)
        ) ||
        meeting.team_member?.name?.toLowerCase().includes(searchLower)
      );
    }),
    [meetings, searchTerm]
  );

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center text-red-500 py-8">{error}</div>;

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">Completed Meetings</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search meetings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid gap-6">
        {filteredMeetings.map((meeting) => (
          <div
            key={meeting.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="font-medium">{meeting.team_member?.name}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(meeting.meeting_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingMeeting({...meeting});
                      setShowEditDialog(true);
                    }}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingMeeting(meeting);
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {meeting.agenda_items?.map((item) => (
                <div key={item.id} className="border-b pb-4 mb-4 last:border-b-0">
                  <div className="mb-2">
                    <h3 className="font-medium">Types</h3>
                    <div className="flex gap-2 flex-wrap">
                      {item.type.map((type, i) => (
                        <span key={i} className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                  {item.discussion_points && (
                    <div className="mb-4">
                      <h3 className="font-medium mb-2">Discussion Points</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.discussion_points}</p>
                    </div>
                  )}
                  {item.next_steps && (
                    <div className="mb-4">
                      <h3 className="font-medium mb-2">Next Steps</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.next_steps}</p>
                    </div>
                  )}
                  {item.blockers && (
                    <div className="mb-4">
                      <h3 className="font-medium mb-2">Blockers</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.blockers}</p>
                    </div>
                  )}
                  {item.notes && (
                    <div>
                      <h3 className="font-medium mb-2">Notes</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {filteredMeetings.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            No completed meetings found
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]" description="Delete meeting confirmation dialog">
          <DialogHeader>
            <DialogTitle>Delete Meeting</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to delete this meeting? This action cannot be undone.
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => editingMeeting && handleDelete(editingMeeting)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Meeting Dialog */}
      <Dialog 
        open={showEditDialog} 
        onOpenChange={(open) => {
          if (!open) {
            setEditingMeeting(null);
          }
          setShowEditDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-[1000px] bg-gray-800 border-gray-700" description="Edit meeting details form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-100">
              <Pencil className="w-5 h-5" />
              Edit Meeting
            </DialogTitle>
          </DialogHeader>
          {editingMeeting && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdate(editingMeeting);
            }}>
              <div className="grid gap-6 py-4 px-6 max-h-[80vh] overflow-y-auto scrollbar-hide">
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-gray-100">Team Member</label>
                  <Select
                    value={editingMeeting.team_member.id}
                    onValueChange={(value) => {
                      const owner = owners.find(o => o.id === value);
                      setEditingMeeting({
                        ...editingMeeting,
                        team_member: {
                          id: value,
                          name: owner?.name || editingMeeting.team_member.name
                        }
                      });
                    }}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-gray-100 h-[42px] focus:ring-1 focus:ring-gray-500">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>
                          {owner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <label className="text-sm font-medium text-gray-100">Meeting Date</label>
                  <DateInput
                    value={editingMeeting.meeting_date}
                    onChange={(e) => setEditingMeeting({
                      ...editingMeeting,
                      meeting_date: e.target.value
                    })}
                    className="bg-gray-700 border-gray-600 text-gray-100 h-[42px] w-full focus:ring-1 focus:ring-gray-500" />
                </div>

                {editingMeeting.agenda_items?.map((item, index) => (
                  <div key={item.id} className="border-b border-gray-600 pb-6 last:border-b-0">
                    <div className="mb-2">
                      <h3 className="font-medium text-gray-100">Types</h3>
                      <div className="flex gap-2 flex-wrap">
                        {item.type.map((type, i) => (
                          <span key={i} className="bg-gray-700 px-2 py-1 rounded text-sm text-gray-100">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <h3 className="font-medium mb-2 text-gray-100">Discussion Points</h3>
                      <Textarea
                        value={item.discussion_points || ''}
                        onChange={(e) => {
                          const updatedItems = [...editingMeeting.agenda_items];
                          updatedItems[index] = {
                            ...item,
                            discussion_points: e.target.value
                          };
                          setEditingMeeting({
                            ...editingMeeting,
                            agenda_items: updatedItems
                          });
                        }}
                        className="min-h-[120px] bg-gray-700 border-gray-600 text-gray-100 resize-none focus:ring-1 focus:ring-gray-500 scrollbar-hide" />
                    </div>
                    <div className="mb-4">
                      <h3 className="font-medium mb-2 text-gray-100">Next Steps</h3>
                      <Textarea
                        value={item.next_steps || ''}
                        onChange={(e) => {
                          const updatedItems = [...editingMeeting.agenda_items];
                          updatedItems[index] = {
                            ...item,
                            next_steps: e.target.value
                          };
                          setEditingMeeting({
                            ...editingMeeting,
                            agenda_items: updatedItems
                          });
                        }}
                        className="min-h-[120px] bg-gray-700 border-gray-600 text-gray-100 resize-none focus:ring-1 focus:ring-gray-500 scrollbar-hide" />
                    </div>
                    <div className="mb-4">
                      <h3 className="font-medium mb-2 text-gray-100">Blockers</h3>
                      <Textarea
                        value={item.blockers || ''}
                        onChange={(e) => {
                          const updatedItems = [...editingMeeting.agenda_items];
                          updatedItems[index] = {
                            ...item,
                            blockers: e.target.value
                          };
                          setEditingMeeting({
                            ...editingMeeting,
                            agenda_items: updatedItems
                          });
                        }}
                        className="min-h-[120px] bg-gray-700 border-gray-600 text-gray-100 resize-none focus:ring-1 focus:ring-gray-500 scrollbar-hide" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-2 text-gray-100">Notes</h3>
                      <Textarea
                        value={item.notes || ''}
                        onChange={(e) => {
                          const updatedItems = [...editingMeeting.agenda_items];
                          updatedItems[index] = {
                            ...item,
                            notes: e.target.value
                          };
                          setEditingMeeting({
                            ...editingMeeting,
                            agenda_items: updatedItems
                          });
                        }}
                        className="min-h-[120px] bg-gray-700 border-gray-600 text-gray-100 resize-none focus:ring-1 focus:ring-gray-500 scrollbar-hide" />
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter className="bg-gray-800 px-6 py-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingMeeting(null);
                  }}
                  disabled={isUpdating}
                  className="text-gray-100 hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isUpdating}
                  className="bg-blue-600 text-gray-100 hover:bg-blue-700"
                >
                  {isUpdating ? 'Saving...' : 'Save changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}